// @flow
import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { NavigationUtil } from 'utils/NavigationUtil';
import { RouteNames } from 'router';
import { HomeActions } from 'modules/home/actions';
import { HomeSelector } from 'modules/home/selector';
import { PageLoader } from 'components/molecules/pageLoader';
import { ProjectAnnouncementsSection } from 'components/templates/projectAnnouncementsSection';
import { ContactUsSection } from 'components/templates/contactUsSection';
import { ProjectSelector } from 'modules/project/selector';
import FooterSection from 'components/templates/footer';
import PageTemplate from 'components/templates/pageTemplate';
import { useViewport } from 'components/templates/viewportProvider';
import type { AppState } from 'modules/rootReducer';
import type { Contact, SiteSettings, Vertical } from 'types/common';
import RenderSEO from 'components/atoms/renderSEO';
import ProjectBrowser from 'components/templates/projectBrowser';
import { ProductSelector } from 'modules/product/selector';
import { LocationSelector } from 'modules/location/selector';
import { ProjectActions } from 'modules/project/actions';
import { LocationActions } from 'modules/location/actions';
import NewsSection from 'components/templates/newsSection';
import { AboutPageSelector } from 'modules/about/selector';
import { AboutActions } from 'modules/about/actions';
import { ArchitectsActions } from 'modules/shibaniKamalArchitecture/actions';
import { ArchitectsSelector } from 'modules/shibaniKamalArchitecture/selector';
import ImageGallerySection from 'components/templates/imageGallerySection';
import { TeamActions } from 'modules/team/action';
import { TeamSelector } from 'modules/team/selector';
import Team from 'components/templates/team';
import { AwardActions } from 'modules/awards/action';
import { AwardSelector } from 'modules/awards/selector';
import Award from 'components/templates/awards';
import VisionPhilosophySection from 'components/templates/vision&PhilosophySection';
import JoinOurTeamSection from 'components/templates/joinOurTeam';
import { CareerSelector } from 'modules/careers/selector';
import { CareerActions } from 'modules/careers/actions';
import { CmsPagesSelector } from 'modules/cmsPages/selector';
import { CmsPagesActions } from 'modules/cmsPages/actions';
import EDesignSection from 'components/templates/eDesign';
import { CmsDetailsSelector } from 'modules/cmsDetails/selector';
import { CmsDetailsActions } from 'modules/cmsDetails/actions';
import CultureLeadershipSection from 'components/templates/cultureLeadershipSection';
import ArchitectureDesignBanner from 'components/organisms/architectureDesignBanner';
import { FooterActions } from 'modules/footerLink/actions';
import { FooterSelector } from 'modules/footerLink/selector';

type Props = {
  isLoading: boolean,
  verticals: Array<Vertical>,
  verticalId: number | null,
  getVerticals: () => void,
  siteSettings: SiteSettings,
  getSiteSettings: () => void,
  contact: Contact,
  getContactDetails: () => void,
};

const ArchitectureDesignPage = (props: Props) => {
  const {
    isLoading,
    getVerticals,
    verticals,
    verticalId,
    getSiteSettings,
    siteSettings,
    getAnnouncements,
    announcements,
    contact,
    getContactDetails,
    getBannerData,
    bannerData,
    getProjects,
    projectList,
    awardData,
    getAwardData,
    getUserLocation,
    userLocation,
    getAboutNews,
    newsData,
    teamData,
    getTeamData,
    getGalleryForArchitecture,
    gallery,
    getPageData,
    visionPhilosophySection,
    jobPosts,
    footerLink,
    getFooterLinkData,
    getJobPosts,
    ourDifferenceData,
    eDesign,
    cmsDetailsData,
    getCmsDetailsData,
    getProjectDetailsForArchitecture,
    projectOverlayData,
  } = props;
  const metaTagSEO = {
    metaTitle: 'Shibanee + Kamal Architects | Total Environment Homes',
    metaDescription:
      'Architecture & Design by Total Environment. Interested in Building out your dream space? Whether its a workspace, venture or a new home. We can help you bring it to life. For more Details, contact us at +91 80 4245 3000',
    metaKeywords: '',
  };

  const { isMobile } = useViewport();
  let [theme, setTheme] = useState(isMobile ? 'dark-theme' : 'light-theme');
  const [mapView, setMapView] = useState(false);

  useEffect(() => {
    getContactDetails();
    getVerticals();
    getSiteSettings();
    getBannerData();
    getProjects(verticalId);
    getAnnouncements(verticalId);
    getUserLocation();
    getAboutNews();
    getAwardData(verticalId);
    getTeamData(verticalId);
    getGalleryForArchitecture();
    getPageData();
    getCmsDetailsData();
    getFooterLinkData();
    getJobPosts(verticalId);
    getProjectDetailsForArchitecture();
  }, []);

  if (isLoading) {
    return <PageLoader isMobile={isMobile} />;
  }

  if (
    !contact ||
    !siteSettings ||
    !verticals ||
    !bannerData ||
    !projectList ||
    !userLocation ||
    !newsData ||
    !gallery ||
    !teamData ||
    !awardData ||
    !visionPhilosophySection ||
    !ourDifferenceData ||
    !eDesign ||
    !cmsDetailsData ||
    !footerLink ||
    !projectOverlayData
  ) {
    return null;
  }

  if (bannerData.metaTitle) metaTagSEO.metaTitle = bannerData.metaTitle;
  if (bannerData.metaDescription)
    metaTagSEO.metaDescription = bannerData.metaDescription;
  if (bannerData.metaKeywords)
    metaTagSEO.metaKeywords = bannerData.metaKeywords;

  const updateMapView = (theme, view) => {
    setTheme(theme);
    setMapView(view);
  };

  const { customiseWithEDesign } = cmsDetailsData;

  const componentList = [
    ...(!isEmpty(bannerData)
      ? [
          {
            component: ArchitectureDesignBanner,
            data: { bannerData },
            theme: isMobile ? 'dark-theme' : 'light-theme',
            sectionId: 'banner',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(visionPhilosophySection.visionSection)
      ? [
          {
            component: VisionPhilosophySection,
            data: {
              visionPhilosophySection,
              showButton: false,
              showCount: true,
              type: 'architecture',
            },
            theme: 'light-theme',
            sectionId: 'design-philosophy',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'Design Philosophy',
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(projectList.list)
      ? [
          {
            component: ProjectBrowser,
            data: {
              data: projectList,
              userLocation,
              updateMapView,
              mapView,
              vertical: 'architects',
              architectProjectOverlayData: projectOverlayData,
              awardData,
              type: 'architecture-projects',
            },
            sectionId: 'projects',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: `Project Browser`,
                breadcrumbLink: '#',
              },
            ],
            theme: isMobile ? 'dark-theme' : 'light-theme',
          },
        ]
      : []),
      ...(!isEmpty(announcements)
      ? [
          {
            component: ProjectAnnouncementsSection,
            data: { announcements },
            sectionId: 'announcements',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'Announcements',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            sectionName: 'announcement-section',
          },
        ]
      : []),
    ...(!isEmpty(teamData)
      ? [
          {
            component: Team,
            sectionId: 'team',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'The Team',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: teamData,
          },
        ]
      : []),
    ...(!isEmpty(newsData.result_news_data)
      ? [
          {
            component: NewsSection,
            data: {
              newsData,
              sectionHeading: 'News',
              overlayButtonTextDesktop: 'SEE ALL NEWS',
              showNotificationButton: false,
              toggleId: false,
            },
            sectionId: 'publications',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'News',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
          },
        ]
      : []),
    ...(!isEmpty(awardData)
      ? [
          {
            component: Award,
            sectionId: 'awards',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'Awards',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
            data: awardData,
          },
        ]
      : []),
    ...(!isEmpty(ourDifferenceData)
      ? [
          {
            component: CultureLeadershipSection,
            data: {
              ourDifferenceData,
              cmsDetailsData,
            },
            sectionId: 'design-services',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'Design Services',
                breadcrumbLink: '#',
              },
            ],
            theme: 'light-theme',
          },
        ]
      : []),
    ...(!isEmpty(gallery)
      ? [
          {
            component: ImageGallerySection,
            data: {
              images: gallery,
            },
            theme: 'dark-theme',
            sectionId: 'gallery',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: `Gallery`,
                breadcrumbLink: '#',
              },
            ],
          },
        ]
      : []),
    ...(!isEmpty(eDesign)
      ? [
          {
            component: EDesignSection,
            data: {
              eDesign,
              customiseWithEDesign,
            },
            sectionId: 'eDesign',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.architectureDesign,
              },
              {
                breadcrumbName: 'eDesign',
                breadcrumbLink: '#',
              },
            ],
            theme: 'light-theme',
          },
        ]
      : []),
    ...(!isEmpty(jobPosts)
      ? [
          {
            component: JoinOurTeamSection,
            data: { jobPosts, showButton: false },
            sectionId: 'open-positions',
            breadcrumbList: [
              {
                breadcrumbName: 'TotalEnvironment',
                breadcrumbLink: RouteNames.landing,
              },
              {
                breadcrumbName: 'Shibanee + Kamal Architects',
                breadcrumbLink: RouteNames.restaurants,
              },
              {
                breadcrumbName: 'Join Our Team',
                breadcrumbLink: '#',
              },
            ],
            theme: 'dark-theme',
          },
        ]
      : []),
    {
      component: ContactUsSection,
      data: { contact, verticals, verticalId },
      location: props,
      sectionId: 'contact',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Shibanee + Kamal Architects',
          breadcrumbLink: RouteNames.architectureDesign,
        },
        {
          breadcrumbName: 'Contact Us',
          breadcrumbLink: '#',
        },
      ],
      theme: 'light-theme',
    },
    {
      component: FooterSection,
      theme: 'dark-theme',
      sectionId: 'te-links',
      breadcrumbList: [
        {
          breadcrumbName: 'TotalEnvironment',
          breadcrumbLink: RouteNames.landing,
        },
        {
          breadcrumbName: 'Shibanee + Kamal Architects',
          breadcrumbLink: RouteNames.architectureDesign,
        },
        {
          breadcrumbName: 'Footer',
          breadcrumbLink: '#',
        },
      ],
      data: { siteSettings, footerLink },
    },
  ];

  return (
    <>
      <RenderSEO metaTags={metaTagSEO} />
      <PageTemplate
        theme={theme}
        componentList={componentList}
        updateTheme={e => setTheme(e)}
        verticals={verticals}
        siteSettings={siteSettings}
      />
    </>
  );
};

const mapStateToProps = (state: AppState, props: any) => {
  const verticalKey = NavigationUtil.getVerticalKeyForLocation(props.location);

  return {
    isLoading:
      HomeSelector.isLoading(state) ||
      AwardSelector.isLoading(state) ||
      FooterSelector.isLoading(state) ||
      ProductSelector.isLoading(state) ||
      AboutPageSelector.getAboutNewsLoading(state) ||
      TeamSelector.getTeamLoading(state) ||
      ArchitectsSelector.isLoading(state) ||
      CmsPagesSelector.isCmsPageLoading(state) ||
      CareerSelector.getJobPostsLoading(state) ||
      CmsDetailsSelector.getCmsDetailsDataLoading(state),
    contact: HomeSelector.getContactForVertical(state, verticalKey),
    verticalId: HomeSelector.getVerticalIdForKey(verticalKey),
    verticals: HomeSelector.getVerticals(state),
    footerLink: FooterSelector.getFooterData(state),
    announcements: ProjectSelector.getAnnouncements(state),
    awardData: AwardSelector.getAwardData(state),
    siteSettings: HomeSelector.getSiteSettings(state),
    bannerData: HomeSelector.getBanners(state, true, 'shibanee', ''),
    projectList: ProductSelector.getProjectsForArchitects(state),
    userLocation: LocationSelector.getUserLocation(state),
    newsData: AboutPageSelector.getAboutNews(state, 'News', 'shibanee'),
    gallery: ArchitectsSelector.getGallery(state),
    teamData: TeamSelector.getTeamData(state),
    visionPhilosophySection: CmsPagesSelector.getVisionPhilosophy(
      state,
      '',
      'design',
      true
    ),
    ourDifferenceData: CmsPagesSelector.getOurDifferenceSection(
      state,
      'design-services',
      'Design Services',
      HomeSelector.getVerticalIdForKey(verticalKey)
    ),
    eDesign: CmsPagesSelector.getCmsPages(
      state,
      'Customise With eDesign',
      false
    ),
    cmsDetailsData: CmsDetailsSelector.getCmsDetailsData(state),
    jobPosts: CareerSelector.getJobPosts(state),
    projectOverlayData: ArchitectsSelector.getProjectOverlay(state),
  };
};

const mapDispatchToProps = dispatch => {
  const {
    getContactDetails,
    getVerticals,
    getSiteSettings,
    getBannerData,
  } = HomeActions;
  const { getTeamData } = TeamActions;
  const { getAwardData } = AwardActions;
  const { getProjects } = ProjectActions;
  const { getUserLocation } = LocationActions;
  const { getAboutNews } = AboutActions;
  const {
    getGalleryForArchitecture,
    getProjectDetailsForArchitecture,
  } = ArchitectsActions;
  const { getPageData } = CmsPagesActions;
  const { getCmsDetailsData } = CmsDetailsActions;
  const { getJobPosts } = CareerActions;
  const { getFooterLinkData } = FooterActions;
  const { getAnnouncements } = ProjectActions;
  return bindActionCreators(
    {
      getContactDetails,
      getVerticals,
      getSiteSettings,
      getBannerData,
      getProjects,
      getUserLocation,
      getAboutNews,
      getTeamData,
      getAnnouncements,
      getAwardData,
      getGalleryForArchitecture,
      getProjectDetailsForArchitecture,
      getPageData,
      getJobPosts,
      getCmsDetailsData,
      getFooterLinkData,
    },
    dispatch
  );
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ArchitectureDesignPage);
